import Link from 'next/link';

import { cn } from '@utils';
import { isExternalStoryblokLink, resolveStoryblokLinkHref } from '@utils/resolveStoryblokLinkHref';

import { Chevron, isPathActive, type MobileOpenState } from './SiteHeader.shared';
import type { SiteSettingsData } from './types';

type SiteHeaderMobileNavigationProps = {
  isOpen: boolean;
  navigation: SiteSettingsData['navigation'];
  onToggleGroup: (label: string) => void;
  openGroups: MobileOpenState;
  pathname: string;
};

export function SiteHeaderMobileNavigation({
  isOpen,
  navigation,
  onToggleGroup,
  openGroups,
  pathname,
}: SiteHeaderMobileNavigationProps) {
  return (
    <div
      className={cn(
        'overflow-hidden bg-muted transition-[max-height] duration-300 lg:hidden',
        isOpen && 'border-t border-primary/10',
        isOpen ? 'max-h-[80vh]' : 'max-h-0',
      )}
      id="mobile-navigation"
    >
      <nav
        aria-label="Mobiele navigatie"
        className="container py-6"
      >
        <div className="space-y-2">
          {navigation.map((item) => {
            const href = resolveStoryblokLinkHref(item.href);
            const hasChildren = item.children != null && item.children.length > 0;

            if (!hasChildren) {
              return (
                <Link
                  className={cn(
                    'block rounded-[1rem] border border-transparent px-4 py-4 text-sm uppercase tracking-[0.18em] text-primary transition-colors',
                    isPathActive(pathname, href) && 'border-primary/10 bg-white',
                  )}
                  href={href}
                  key={item.label}
                >
                  {item.label}
                </Link>
              );
            }

            const isGroupOpen = openGroups[item.label] ?? false;

            return (
              <div
                className="rounded-[1rem] border border-primary/10 bg-white/70"
                key={item.label}
              >
                <div className="flex items-center justify-between gap-3">
                  <Link
                    className="flex-1 px-4 py-4 text-sm uppercase tracking-[0.18em] text-primary"
                    href={href}
                  >
                    {item.label}
                  </Link>
                  <button
                    aria-expanded={isGroupOpen}
                    aria-label={
                      isGroupOpen ? `Sluit submenu ${item.label}` : `Open submenu ${item.label}`
                    }
                    className="px-4 py-4 text-primary"
                    onClick={() => onToggleGroup(item.label)}
                    type="button"
                  >
                    <Chevron open={isGroupOpen} />
                  </button>
                </div>

                <div
                  className={cn(
                    'grid transition-[grid-template-rows] duration-200',
                    isGroupOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
                  )}
                >
                  <div className="overflow-hidden">
                    <div className="space-y-2 border-t border-primary/10 px-3 pb-3 pt-1">
                      {item.children?.map((child) => {
                        const childHref = resolveStoryblokLinkHref(child.href);
                        const childIsExternal = isExternalStoryblokLink(child.href);

                        return (
                          <Link
                            className={cn(
                              'block rounded-[0.9rem] px-3 py-3 text-primary transition-colors',
                              isPathActive(pathname, childHref)
                                ? 'bg-muted text-primary'
                                : 'hover:bg-[#f7f4ef] hover:text-primary',
                            )}
                            href={childHref}
                            key={child.label}
                            rel={childIsExternal ? 'noreferrer noopener' : undefined}
                            target={childIsExternal ? '_blank' : undefined}
                          >
                            <span className="block text-sm uppercase tracking-[0.14em]">
                              {child.label}
                            </span>
                            {child.description && (
                              <span className="mt-1 block text-sm normal-case tracking-normal text-primary">
                                {child.description}
                              </span>
                            )}
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
